import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { QuestionnaireService } from "../questionnaire.service";

// AR-24  Patient answers her pre-exam questionnaire on her phone.
// Screen flow: 'locked' (already answered, link closed) | 'id' (תז) -> 'form' (answer) -> 'done' (thank-you).
// AR-31: a link is single-use. Once answered it reports `locked` from getByCode
// and the patient never reaches 'id'/'form' again — only staff resending the
// questionnaire (which reopens the same link) gets her back to 'id'/'form'.
//
// AR-32: a question can be a `repeatableGroup` — a parent holding nested
// `subQuestions` that repeat N times (e.g. "details of each previous
// delivery"). That breaks the single "one answer per question, keyed by
// AutoIndex" assumption this component used to make everywhere, so:
//   - `answers`/`otherText` are keyed by a STRING path, not a bare AutoIndex:
//     `String(q.AutoIndex)` at the top level, `${groupId}:${row}:${subId}`
//     inside a group row.
//   - the actual per-type input markup lives in <app-question-field>
//     (question-field.component.ts) so the same ~80-line render chain can be
//     reused for a top-level question AND for every row of a group, each
//     with its own key.
@Component({
  selector: "app-questionnaire",
  templateUrl: "./questionnaire.component.html",
  styleUrls: ["./questionnaire.component.scss"],
  standalone: false,
})
export class QuestionnaireComponent implements OnInit {
  code = "";
  loading = true;
  loadError = false;

  clinicName = "";
  examCode = "";
  questionnaire: any = null;
  answeredDate: string | null = null;

  screen: "locked" | "id" | "form" | "done" = "id";

  // ---- identity ----
  ID = "";
  idError = false;      // failed local checksum / format
  verifying = false;
  verifyError = false;  // תז did not match the record

  // ---- answering ----
  lang: "he" | "en" = "he";
  answers: { [key: string]: any } = {};
  otherText: { [key: string]: string } = {};
  submitting = false;
  submitError = false;
  dateRangeError = false;
  attemptedSubmit = false;

  prefillData: any = null;

  // AR-32: current row count per repeatableGroup, keyed by the group's own
  // AutoIndex. A row "existing" means its answer keys have been materialized
  // in `answers`/`otherText` — nothing implicitly derives this from the
  // answers map, it's tracked explicitly so add/remove/derive-from-counter
  // all have one place to update.
  private groupRowCounts: { [groupAutoIndex: number]: number } = {};
  // Session-only buffer of trimmed rows (AR-32), so raising a counter back up
  // (or re-adding a removed row) restores what was there rather than showing
  // blanks. Keyed by `${groupAutoIndex}:${row}`, value is per-sub-question
  // AutoIndex -> answer value (otherText is intentionally not buffered — an
  // acceptable loss on trim, the main answer is what matters).
  private groupRowBuffers: { [bufferKey: string]: { [subAutoIndex: number]: any } } = {};
  static readonly MAX_GROUP_ROWS = 20;

  constructor(
    private route: ActivatedRoute,
    private qService: QuestionnaireService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.translate.use("he-IL");
    this.code = this.route.snapshot.paramMap.get("code") || "";
    if (!this.code) {
      this.loading = false;
      this.loadError = true;
      return;
    }
    this.qService.getByCode(this.code).subscribe({
      next: (data: any) => {
        this.loading = false;
        this.clinicName = data.clinicName || "";
        this.examCode = data.examCode || "";
        if (data.locked) {
          this.answeredDate = data.answeredDate || null;
          this.screen = "locked";
          return;
        }
        this.questionnaire = data.questionnaire;
        const dl = (this.questionnaire && this.questionnaire.defaultLanguage) || "he";
        this.lang = dl === "en" ? "en" : "he";
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  // Standard Israeli ID checksum (lifted from appointment-setup).
  IDValidator(): boolean {
    const id: any = (this.ID || "").replace(/-/g, "");
    if (id.length < 9 || isNaN(id)) return false;
    let sum = 0;
    for (let i = 0; i < id.length; i++) {
      let inc = Number(id[i]) * ((i % 2) + 1);
      sum += inc > 9 ? inc - 9 : inc;
    }
    return sum % 10 === 0;
  }

  verify(): void {
    this.idError = false;
    this.verifyError = false;
    if (!this.IDValidator()) {
      this.idError = true;
      return;
    }
    this.verifying = true;
    this.qService.verify(this.code, this.ID.replace(/-/g, "")).subscribe({
      next: (res: any) => {
        this.verifying = false;
        this.initAnswers();
        this.prefillData = (res && res.prefill) || null;
        this.applyPrefill();
        this.applyPreviousAnswers((res && res.answers) || []);
        this.screen = "form";
      },
      error: (err: any) => {
        this.verifying = false;
        // Race backstop: the link was answered (e.g. in another tab) between
        // load and verify — send her to the locked screen instead of a
        // misleading "ID does not match".
        if (err && err.status === 409) {
          this.screen = "locked";
          return;
        }
        this.verifyError = true;
      },
    });
  }

  // ---- keys ----
  // A plain top-level question's answer lives under its bare AutoIndex
  // (stringified, since `answers` is keyed by string path everywhere now).
  qKey(q: any): string {
    return String(q.AutoIndex);
  }
  // A repeatableGroup row's sub-answer needs group + row + sub-question all
  // three, or two different rows (or two different groups' same-shaped
  // sub-question) would collide on the same key.
  groupKey(group: any, row: number, sq: any): string {
    return `${group.AutoIndex}:${row}:${sq.AutoIndex}`;
  }

  initAnswers(): void {
    this.answers = {};
    this.otherText = {};
    this.groupRowCounts = {};
    this.groupRowBuffers = {};
    for (const q of this.questions()) {
      if (this.isContentBlock(q.questionType)) continue;
      if (q.questionType === "repeatableGroup") continue; // rows materialized once counts are known (see applyPreviousAnswers/onFieldCommitted)
      this.answers[this.qKey(q)] = q.questionType === "checkbox" ? [] : "";
    }
  }

  // ---- repeatable groups (AR-32) ----
  groupRowIndexes(group: any): number[] {
    const n = this.groupRowCounts[group.AutoIndex] ?? 0;
    return Array.from({ length: n }, (_, i) => i);
  }

  private clampRowCount(n: number): number {
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(QuestionnaireComponent.MAX_GROUP_ROWS, Math.floor(n));
  }

  private bufferKey(group: any, row: number): string {
    return `${group.AutoIndex}:${row}`;
  }

  private bufferRow(group: any, row: number): void {
    const snapshot: { [subAutoIndex: number]: any } = {};
    for (const sq of group.subQuestions || []) {
      const k = this.groupKey(group, row, sq);
      if (this.answers[k] !== undefined) snapshot[sq.AutoIndex] = this.answers[k];
    }
    this.groupRowBuffers[this.bufferKey(group, row)] = snapshot;
  }

  private clearRow(group: any, row: number): void {
    for (const sq of group.subQuestions || []) {
      const k = this.groupKey(group, row, sq);
      delete this.answers[k];
      Object.keys(this.otherText).forEach((ok) => {
        if (ok === k || ok.startsWith(k + ":")) delete this.otherText[ok];
      });
    }
  }

  private materializeRow(group: any, row: number): void {
    const buffered = this.groupRowBuffers[this.bufferKey(group, row)];
    for (const sq of group.subQuestions || []) {
      const k = this.groupKey(group, row, sq);
      if (this.answers[k] !== undefined) continue;
      if (buffered && buffered[sq.AutoIndex] !== undefined) {
        this.answers[k] = buffered[sq.AutoIndex];
      } else {
        this.answers[k] = sq.questionType === "checkbox" ? [] : "";
      }
    }
  }

  // Set a group's row count directly to `n` (clamped 0-20). Trimmed rows are
  // buffered (not lost) so raising the count back up restores them; new rows
  // beyond the buffer get type-correct blanks.
  private setGroupRowCount(group: any, n: number): void {
    const key = group.AutoIndex;
    const current = this.groupRowCounts[key] ?? 0;
    n = this.clampRowCount(n);
    if (n === current) return;
    if (n < current) {
      for (let row = n; row < current; row++) {
        this.bufferRow(group, row);
        this.clearRow(group, row);
      }
    } else {
      for (let row = current; row < n; row++) {
        this.materializeRow(group, row);
      }
    }
    this.groupRowCounts[key] = n;
  }

  // Manual mode only (no repeatCountFromQuestionId): patient-driven +/-.
  addRow(group: any): void {
    this.setGroupRowCount(group, (this.groupRowCounts[group.AutoIndex] ?? 0) + 1);
  }
  removeRow(group: any, row: number): void {
    const current = this.groupRowCounts[group.AutoIndex] ?? 0;
    if (row >= current) return;
    // Shift every later row down by one so indices stay contiguous 0..n-1 -
    // unlike setGroupRowCount's trim (which only ever drops from the end).
    for (let r = row; r < current - 1; r++) {
      for (const sq of group.subQuestions || []) {
        const fromKey = this.groupKey(group, r + 1, sq);
        const toKey = this.groupKey(group, r, sq);
        this.answers[toKey] = this.answers[fromKey];
      }
    }
    this.clearRow(group, current - 1);
    this.groupRowCounts[group.AutoIndex] = current - 1;
  }

  // Auto mode: re-derive every group's row count from its counter question's
  // current answer. Called on a *committed* change (see
  // question-field.component's `committed` output) rather than on every
  // keystroke, so typing "12" into the counter doesn't destroy 11 rows the
  // instant "1" is on screen.
  onFieldCommitted(): void {
    for (const q of this.questions()) {
      if (q.questionType !== "repeatableGroup" || !q.repeatCountFromQuestionId) continue;
      const raw = this.answers[String(q.repeatCountFromQuestionId)];
      this.setGroupRowCount(q, Number(raw) || 0);
    }
  }

  // Prefill inputs with the patient's previous answers (returned by verify).
  // Checkbox values arrive already parsed into arrays from the API.
  //
  // AR-32 ordering matters: a group row's answer key doesn't exist until the
  // row itself has been materialized, and the row count for an auto-mode
  // group is only known once its counter question's OWN previous answer has
  // been applied. So: apply every top-level answer first (this is what fills
  // in the counter), THEN size every group's rows, THEN apply group answers.
  applyPreviousAnswers(prev: any[]): void {
    const topLevel = (prev || []).filter((a) => a.groupIndex == null);
    const grouped = (prev || []).filter((a) => a.groupIndex != null);

    this.applyAnswerList(topLevel, (a) => (a.questionId != null ? this.qKey({ AutoIndex: a.questionId }) : null));

    for (const q of this.questions()) {
      if (q.questionType !== "repeatableGroup") continue;
      const subIds = new Set((q.subQuestions || []).map((sq: any) => sq.AutoIndex));
      const maxGroupIndex = grouped
        .filter((a) => subIds.has(a.questionId))
        .reduce((max, a) => Math.max(max, a.groupIndex), -1);
      const derivedFromCounter = q.repeatCountFromQuestionId
        ? Number(this.answers[String(q.repeatCountFromQuestionId)]) || 0
        : 0;
      this.setGroupRowCount(q, Math.max(maxGroupIndex + 1, derivedFromCounter, q.minItems || 0));
    }

    this.applyAnswerList(grouped, (a) => this.resolveGroupAnswerKey(a));
  }

  private resolveGroupAnswerKey(a: any): string | null {
    for (const q of this.questions()) {
      if (q.questionType !== "repeatableGroup") continue;
      const sq = (q.subQuestions || []).find((s: any) => s.AutoIndex === a.questionId);
      if (sq) return this.groupKey(q, a.groupIndex, sq);
    }
    return null;
  }

  // Finds the question definition (top-level OR nested inside a group) for a
  // given AutoIndex, so a previous-answer row can look up its questionType
  // (needed to know whether to split "option – extra text").
  private findQuestion(autoIndex: number): any {
    for (const q of this.questions()) {
      if (q.AutoIndex === autoIndex) return q;
      if (q.questionType === "repeatableGroup") {
        const sq = (q.subQuestions || []).find((s: any) => s.AutoIndex === autoIndex);
        if (sq) return sq;
      }
    }
    return null;
  }

  private applyAnswerList(list: any[], keyFn: (a: any) => string | null): void {
    for (const a of list) {
      if (a.questionId == null) continue;
      const key = keyFn(a);
      if (key == null) continue;
      if (a.answerValue === null || a.answerValue === undefined) continue;

      const q = this.findQuestion(a.questionId);
      if (!q) { this.answers[key] = a.answerValue; continue; }

      if (q.questionType === "checkbox" && Array.isArray(a.answerValue)) {
        this.answers[key] = a.answerValue.map((raw: string) => {
          const { optionText, extra } = this.splitExtraText(q, raw);
          if (extra) this.otherText[`${key}:${optionText}`] = extra;
          return optionText;
        });
      } else if (q.questionType === "radio" || q.questionType === "select") {
        const { optionText, extra } = this.splitExtraText(q, a.answerValue);
        this.answers[key] = optionText;
        if (extra) this.otherText[key] = extra;
      } else {
        this.answers[key] = a.answerValue;
      }
    }
  }

  // Reverses the "<optionText> – <extra>" combination answerValueFor() builds on
  // submit, so a reopened questionnaire restores both the picked option AND the
  // free-text/number the patient typed into its "other"-style extra field.
  private splitExtraText(q: any, raw: any): { optionText: any; extra: string } {
    const sep = " – ";
    if (typeof raw === "string") {
      for (const o of q.options || []) {
        if (this.extraFieldType(o) && raw.startsWith(o.optionText + sep)) {
          return { optionText: o.optionText, extra: raw.slice((o.optionText + sep).length) };
        }
      }
    }
    return { optionText: raw, extra: "" };
  }

  applyPrefill(): void {
    if (!this.prefillData) return;
    for (const q of this.questions()) {
      if (this.isContentBlock(q.questionType) || q.questionType === "repeatableGroup" || !q.prefillField) continue;
      const current = this.answers[this.qKey(q)];
      const isBlank = current === "" || current === null || current === undefined;
      if (isBlank && this.prefillData[q.prefillField]) {
        this.answers[this.qKey(q)] = this.prefillData[q.prefillField];
      }
    }
  }

  // ---- rendering helpers ----
  questions(): any[] {
    return (this.questionnaire && this.questionnaire.questions) || [];
  }
  bothLanguages(): boolean {
    return !!this.questionnaire && this.questionnaire.defaultLanguage === "both";
  }
  // AR-32: each question now renders inside its own component/wrapper element,
  // so CSS ":first-child" can no longer tell "first visible question on the
  // page" from "first (only) child of my own wrapper" - compute it explicitly
  // and pass it down instead (see question-field.component.ts's `first` input).
  isFirst(q: any): boolean {
    return this.questions()[0] === q;
  }
  isContentBlock(t: string): boolean {
    return t === "opening" || t === "closing";
  }
  dir(): string {
    return this.lang === "en" ? "ltr" : "rtl";
  }
  qText(q: any): string {
    return this.lang === "en" && q.questionTextEng ? q.questionTextEng : q.questionText;
  }
  renderBlock(q: any): string {
    let t = this.qText(q) || "";
    const map: { [k: string]: string } = {
      "{{clinicName}}": this.clinicName || "",
      "{{examName}}": this.examCode || "",
      "{{date}}": new Date().toLocaleDateString("he-IL"),
      "{{patientName}}": (this.prefillData && this.prefillData.patientName) || "",
      "{{patientId}}": (this.prefillData && this.prefillData.patientId) || "",
      "{{doctorName}}": (this.prefillData && this.prefillData.doctorName) || "",
    };
    for (const k of Object.keys(map)) t = t.split(k).join(map[k]);
    return t;
  }

  // '' | 'text' | 'number' — used only by the group-row required check below;
  // question-field.component.ts has its own copy for rendering.
  private extraFieldType(o: any): string {
    return (o && (o.freeTextType || (o.allowFreeText ? "text" : ""))) || "";
  }

  // ---- validation ----
  // AR-32: these used to walk `questions()` flatly, keyed bare by AutoIndex.
  // Now every check needs an explicit key, and a group's required sub-question
  // is only checked against rows that actually exist - a required field
  // inside a group with 0 rows must not block submission forever.
  private isAnswerMissing(q: any, key: string): boolean {
    const v = this.answers[key];
    if (q.questionType === "checkbox") return !v || !v.length;
    return v === null || v === undefined || v === "";
  }

  isInvalid(q: any, key: string): boolean {
    return this.attemptedSubmit && !!q.required && this.isAnswerMissing(q, key);
  }

  private requiredMissing(): boolean {
    for (const q of this.questions()) {
      if (this.isContentBlock(q.questionType)) continue;
      if (q.questionType === "repeatableGroup") {
        for (const row of this.groupRowIndexes(q)) {
          for (const sq of q.subQuestions || []) {
            if (!sq.required) continue;
            if (this.isAnswerMissing(sq, this.groupKey(q, row, sq))) return true;
          }
        }
        continue;
      }
      if (!q.required) continue;
      if (this.isAnswerMissing(q, this.qKey(q))) return true;
    }
    return false;
  }

  private dateOutOfRange(q: any, key: string): boolean {
    if (q.questionType !== "date") return false;
    const value = this.answers[key];
    if (!value) return false;
    const min = this.dateBoundFor(q, "dateMin");
    const max = this.dateBoundFor(q, "dateMax");
    return (!!min && value < min) || (!!max && value > max);
  }
  private dateBoundFor(q: any, which: "dateMin" | "dateMax"): string {
    const bound = q[which];
    if (!bound) return "";
    if (bound === "today") {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }
    return bound;
  }

  private dateRangeInvalid(): boolean {
    for (const q of this.questions()) {
      if (q.questionType === "repeatableGroup") {
        for (const row of this.groupRowIndexes(q)) {
          for (const sq of q.subQuestions || []) {
            if (this.dateOutOfRange(sq, this.groupKey(q, row, sq))) return true;
          }
        }
        continue;
      }
      if (this.dateOutOfRange(q, this.qKey(q))) return true;
    }
    return false;
  }

  // ---- submit ----
  private answerValueFor(q: any, key: string): any {
    const raw = this.answers[key];
    if (q.questionType === "checkbox") {
      return (raw || []).map((val: string) => {
        const extra = (this.otherText[`${key}:${val}`] || "").trim();
        return extra ? `${val} – ${extra}` : val;
      });
    }
    if (q.questionType === "radio" || q.questionType === "select") {
      const opt = (q.options || []).find((o: any) => o.optionText === raw);
      const extra = opt && this.extraFieldType(opt) ? (this.otherText[key] || "").trim() : "";
      return extra ? `${raw} – ${extra}` : raw;
    }
    return raw;
  }

  submit(): void {
    this.submitError = false;
    this.dateRangeError = false;
    this.attemptedSubmit = true;
    if (this.requiredMissing()) {
      this.submitError = true;
      return;
    }
    if (this.dateRangeInvalid()) {
      this.dateRangeError = true;
      return;
    }
    this.submitting = true;

    const answers: any[] = [];
    for (const q of this.questions()) {
      if (this.isContentBlock(q.questionType)) continue;
      if (q.questionType === "repeatableGroup") {
        for (const row of this.groupRowIndexes(q)) {
          for (const sq of q.subQuestions || []) {
            const key = this.groupKey(q, row, sq);
            answers.push({ questionId: sq.AutoIndex, answerValue: this.answerValueFor(sq, key), groupIndex: row });
          }
        }
        continue;
      }
      answers.push({ questionId: q.AutoIndex, answerValue: this.answerValueFor(q, this.qKey(q)) });
    }

    this.qService.submit(this.code, this.ID.replace(/-/g, ""), this.lang, answers).subscribe({
      next: () => {
        this.submitting = false;
        this.screen = "done";
      },
      error: (err: any) => {
        this.submitting = false;
        // Race backstop: already submitted elsewhere (e.g. another tab) — send
        // her to the locked screen instead of a misleading validation error.
        if (err && err.status === 409) {
          this.screen = "locked";
          return;
        }
        this.submitError = true;
      },
    });
  }
}
